import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navigation from './components/Navigation';
import Home from './components/Home';
import About from './components/About';
import Schedule from './components/Schedule';
import FaceToFaceMeetings from './components/FaceToFaceMeetings';
import Speakers from './components/Speakers';
import WhyOman from './components/WhyOman';
import WhyOmanMap from './components/WhyOmanMap';
import OpportunitiesInOman from './components/OpportunitiesInOman';
import Sponsors from './components/Sponsors';
import Footer from './components/Footer';
import ProfilePage from './pages/ProfilePage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminOnlyRoute from './components/AdminOnlyRoute';
import OrganizerPathGuard from './components/OrganizerPathGuard';
import { initGA, logPageView } from './utils/analytics';
import { checkNotificationPermission, setupLivestreamNotifications } from './utils/notifications';
import './App.css';

// Lazy load dashboard and admin components to reduce initial bundle size
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Overview = React.lazy(() => import('./pages/dashboard/Overview'));
const Programs = React.lazy(() => import('./pages/dashboard/Programs'));
const ScheduleManagement = React.lazy(() => import('./pages/dashboard/Schedule'));
const DownloadGuide = React.lazy(() => import('./pages/dashboard/DownloadGuide'));
const SpeakersManagement = React.lazy(() => import('./pages/dashboard/Speakers'));
const SponsorsManagement = React.lazy(() => import('./pages/dashboard/Sponsors'));
const MeetingRooms = React.lazy(() => import('./pages/dashboard/MeetingRooms'));
const Attendance = React.lazy(() => import('./pages/dashboard/Attendance'));
const Applicants = React.lazy(() => import('./pages/dashboard/Applicants'));
const Guests = React.lazy(() => import('./pages/dashboard/Guests'));
const SeatAllocation = React.lazy(() => import('./pages/dashboard/SeatAllocation'));
const LivestreamManagement = React.lazy(() => import('./pages/dashboard/Livestream'));
const SuperAdmin = React.lazy(() => import('./pages/dashboard/SuperAdmin'));
const ProfileSetup = React.lazy(() => import('./pages/dashboard/ProfileSetup'));
const ApplicantSetup = React.lazy(() => import('./pages/dashboard/ApplicantSetup'));
const TravelDetails = React.lazy(() => import('./pages/dashboard/TravelDetails'));
const Hotel = React.lazy(() => import('./pages/dashboard/Hotel'));
const VisaLetter = React.lazy(() => import('./pages/dashboard/VisaLetter'));
const Logistics = React.lazy(() => import('./pages/dashboard/Logistics'));
const DriverPickup = React.lazy(() => import('./pages/dashboard/DriverPickup'));
const Payments = React.lazy(() => import('./pages/dashboard/Payments'));
const Analytics = React.lazy(() => import('./pages/dashboard/Analytics'));
const Announcements = React.lazy(() => import('./pages/dashboard/Announcements'));
const Export = React.lazy(() => import('./pages/dashboard/Export'));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Page transition variants
const pageTransition = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      delay: 0.2,
      ease: "easeInOut"
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

// Content container with scroll-triggered animations
const SectionContainer = ({ children, id, className = '' }) => {
  return (
    <motion.section
      id={id}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      transition={{ staggerChildren: 0.2 }}
      className={className ? `section-container ${className}` : 'section-container'}
    >
      {children}
    </motion.section>
  );
};

// Google Analytics measurement ID from environment variables
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Google Analytics page view tracking component
const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (GA_MEASUREMENT_ID) {
      logPageView();
    }
  }, [location]);

  return null;
};

// Animated background component with smooth, elegant motion - GPU optimized
const AnimatedBackground = () => (
  <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-slate-100" style={{ contain: 'strict' }}>
    {/* Deeper, richer moving orbs - GPU accelerated */}
    <div
      className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full mix-blend-multiply blur-[80px] opacity-35"
      style={{
        backgroundColor: '#A78BFA',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
    <div
      className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full mix-blend-multiply blur-[80px] opacity-35"
      style={{
        backgroundColor: '#5EEAD4',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
    <div
      className="absolute -bottom-32 left-[10%] w-[60%] h-[60%] rounded-full mix-blend-multiply blur-[80px] opacity-35"
      style={{
        backgroundColor: '#C4B5FD',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
    <div
      className="absolute -bottom-40 right-[10%] w-[50%] h-[50%] rounded-full mix-blend-multiply blur-[80px] opacity-35"
      style={{
        backgroundColor: '#99F6E4',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    />
  </div>
);

// App content component that includes the route tracker
const AppContent = () => {
  const location = useLocation();
  const isProfilePage = location.pathname === '/profile';
  const isAuthRoute = location.pathname.startsWith('/dashboard');

  // Standalone Profile page (outside dashboard) — for all authenticated users
  if (isProfilePage) {
    return (
      <div className="App admin-layout">
        <RouteChangeTracker />
        <motion.main
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={pageTransition}
        >
          <Routes>
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          </Routes>
        </motion.main>
      </div>
    );
  }

  if (isAuthRoute) {
    return (
      <div className="App admin-layout">
        <RouteChangeTracker />
        <motion.main
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={pageTransition}
        >
          <React.Suspense fallback={<LoadingFallback />}>
            <OrganizerPathGuard>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminOnlyRoute>
                      <Dashboard />
                    </AdminOnlyRoute>
                  </ProtectedRoute>
                }
              />
              <Route path="/dashboard/overview" element={<ProtectedRoute><AdminOnlyRoute><Overview /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/programs" element={<ProtectedRoute><AdminOnlyRoute><Programs /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/schedule" element={<ProtectedRoute><AdminOnlyRoute><ScheduleManagement /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/download-guide" element={<ProtectedRoute><AdminOnlyRoute><DownloadGuide /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/speakers" element={<ProtectedRoute><AdminOnlyRoute><SpeakersManagement /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/sponsors" element={<ProtectedRoute><AdminOnlyRoute><SponsorsManagement /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/meeting-rooms" element={<ProtectedRoute><AdminOnlyRoute><MeetingRooms /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/attendance" element={<ProtectedRoute><AdminOnlyRoute><Attendance /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/applicants" element={<ProtectedRoute><AdminOnlyRoute><Applicants /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/guests" element={<ProtectedRoute><AdminOnlyRoute><Guests /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/seat-allocation" element={<ProtectedRoute><AdminOnlyRoute><SeatAllocation /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/livestream" element={<ProtectedRoute><AdminOnlyRoute><LivestreamManagement /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/super-admin" element={<ProtectedRoute><AdminOnlyRoute><SuperAdmin /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/logistics" element={<ProtectedRoute><AdminOnlyRoute><Logistics /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/driver-pickup" element={<ProtectedRoute><AdminOnlyRoute><DriverPickup /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/payments" element={<ProtectedRoute><AdminOnlyRoute><Payments /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/analytics" element={<ProtectedRoute><AdminOnlyRoute><Analytics /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/announcements" element={<ProtectedRoute><AdminOnlyRoute><Announcements /></AdminOnlyRoute></ProtectedRoute>} />
              <Route path="/dashboard/export" element={<ProtectedRoute><AdminOnlyRoute><Export /></AdminOnlyRoute></ProtectedRoute>} />
              
              <Route path="/dashboard/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
              <Route path="/dashboard/applicant-setup" element={<ProtectedRoute><ApplicantSetup /></ProtectedRoute>} />
              <Route path="/dashboard/applicant-payment" element={<ProtectedRoute><ApplicantSetup /></ProtectedRoute>} />
              <Route path="/dashboard/travel-details" element={<ProtectedRoute><TravelDetails /></ProtectedRoute>} />
              <Route path="/dashboard/hotel" element={<ProtectedRoute><Hotel /></ProtectedRoute>} />
              <Route path="/dashboard/visa-letter" element={<ProtectedRoute><VisaLetter /></ProtectedRoute>} />
              <Route path="/dashboard/profile" element={<ProtectedRoute><Navigate to="/profile" replace /></ProtectedRoute>} />
            </Routes>
            </OrganizerPathGuard>
          </React.Suspense>
        </motion.main >
      </div >
    );
  }

return (
  <div className="App min-h-screen relative font-plus-jakarta flex flex-col">
    <AnimatedBackground />
    <div className="relative w-full flex-grow flex flex-col min-h-screen z-10">
      <RouteChangeTracker />
      <Navigation />
      <motion.main
        className="flex-grow relative z-0"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={pageTransition}
      >
        <Routes>
          <Route path="/" element={
            <>
              <SectionContainer id="home">
                <Home />
              </SectionContainer>
              <SectionContainer id="about"><About /></SectionContainer>
              <SectionContainer id="why-oman"><WhyOman /></SectionContainer>
              <SectionContainer id="speakers"><Speakers /></SectionContainer>
              <SectionContainer id="schedule"><Schedule /></SectionContainer>
              <SectionContainer id="face-to-face-meetings">
                <FaceToFaceMeetings />
              </SectionContainer>
              <SectionContainer id="why-oman-infrastructure" className="bg-[#F9F9FB]">
                <div className="scroll-mt-28 bg-[#F9F9FB]">
                  <div className="mx-auto w-full max-w-[1344px] px-6 pb-[50px] sm:px-10 lg:px-[140px] lg:pb-[70px]">
                    <WhyOmanMap />
                  </div>
                </div>
              </SectionContainer>
              <SectionContainer id="opportunities" className="bg-[#F9F9FB]">
                <OpportunitiesInOman />
              </SectionContainer>
              <SectionContainer id="sponsors"><Sponsors /></SectionContainer>
            </>
          } />
        </Routes>
      </motion.main>
      <Footer />
    </div>
  </div>
);
};

function App() {
  useEffect(() => {
    // Initialize Google Analytics only if measurement ID exists
    if (GA_MEASUREMENT_ID) {
      initGA(GA_MEASUREMENT_ID);
    } else {
      console.warn('Google Analytics Measurement ID not found. Analytics will not be initialized.');
    }

    // Initialize notifications if permission already granted
    const hasNotificationPermission = checkNotificationPermission();
    if (hasNotificationPermission) {
      setupLivestreamNotifications();
    }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
